import type { UserRoleRow } from '../../../shared/components/user-role-row-list/user-role-row-list.component';
import type { CompanyDto } from '../../../shared/companies/models/company.models';
import type { IndustryDto } from '../../../shared/industries/models/industry.models';
import type { MomentumUser } from '../../../shared/models/momentum-user.models';
import { displayTextOr } from '../../../shared/string.util';
import { FRAME_CONTRACT_OPPORTUNITY_FILTER_LABEL } from '../opportunity-frame-contract.labels';
import type { OpportunityWizardStep } from './opportunity-wizard-shell.models';
import type {
  OpportunityAccountPotential,
  OpportunityExistingRelationship,
} from './opportunity-commercial.models';

export const DEAL_SIZES = [
  { value: 'S', label: 'S — less than CHF 100k' },
  { value: 'M', label: 'M — less than CHF 1M' },
  { value: 'L', label: 'L — less than CHF 3M' },
  { value: 'XL', label: 'XL — CHF 3M or more' },
  { value: 'NV', label: FRAME_CONTRACT_OPPORTUNITY_FILTER_LABEL },
] as const;

/** AppApi `ProcurementRoute` wire values (opportunities only — not `ALL`). */
export const PROCUREMENT_ROUTE_VALUES = ['ByInvitation', 'PublicTender'] as const;

export type ProcurementRoute = (typeof PROCUREMENT_ROUTE_VALUES)[number];

export const PROCUREMENT_ROUTE_OPTIONS = [
  {
    value: 'ByInvitation',
    label: 'By invitation',
    description: 'The client sent the request directly to us - we have been invited to propose.',
  },
  {
    value: 'PublicTender',
    label: 'Public tender',
    description: 'The request has been published publicly and is open to any bidder.',
  },
] as const satisfies readonly {
  value: ProcurementRoute;
  label: string;
  description: string;
}[];

export type ProcurementRouteOption = (typeof PROCUREMENT_ROUTE_OPTIONS)[number];

export function procurementRouteLabel(route: ProcurementRoute | null | undefined): string {
  if (!route) {
    return '—';
  }
  const label = PROCUREMENT_ROUTE_OPTIONS.find((option) => option.value === route)?.label;
  return displayTextOr(label, route);
}

/** Empty string = not set (API Unspecified). NV = frame contracts (API Nv). */
export type DealSize = (typeof DEAL_SIZES)[number]['value'] | '';

export type ConfidentialityLevel = 'confidential' | 'strictly-confidential';

export interface ConfidentialityOption {
  value: ConfidentialityLevel;
  label: string;
  description: string;
  disabled?: boolean;
}

export const CONFIDENTIALITY_OPTIONS: readonly ConfidentialityOption[] = [
  {
    value: 'confidential',
    label: 'Confidential',
    description:
      'Visible to bid team members and all Zühlke staff. Standard classification for most proposals.',
  },
  {
    value: 'strictly-confidential',
    label: 'Strictly Confidential - COMING SOON',
    description:
      'Bid team access only. Use for sensitive, NDA-bound, or competitively critical engagements.',
    disabled: true,
  },
];

export type OpportunityPipelineStage = 'qualify' | 'develop' | 'closed';

export type OpportunityBidTeamMember = UserRoleRow;

/** Opportunity Assessment go / no-go / re-qualify; null when Pending or not set. */
export type OpportunityAssessmentDecision = 'go' | 'no-go' | 'back-to-qual' | null;

export interface OpportunityMilestones {
  msQualify?: string;
  msAlpha?: string;
  msApproval?: string;
  msSubmission?: string;
  msClose?: string;
}

export interface OpportunityFormData {
  title: string;
  client: string;
  crmOpportunityId?: string;
  crmProjectId?: string;
  cNumber?: string;
  confidentiality: ConfidentialityLevel;
  dealSize: DealSize;
  submissionDeadline?: string;
  description?: string;
  opportunityOwner?: MomentumUser | null;
  proposalManager?: MomentumUser | null;
  bidTeamMembers?: OpportunityBidTeamMember[];
  industry?: IndustryDto;
  company?: CompanyDto;
  procurementRoute?: ProcurementRoute | null;
  isFrameContract?: boolean;
  /** Opportunity currency code (API `currency`; read-only in metadata). */
  currency?: string;
  /** Local TCV in `currency` (API `tcvLocal`; editable in metadata). */
  tcvLocal?: number | null;
  /** Base TCV in CHF from CRM (API `tcv`; read-only in metadata). */
  tcv?: number | null;
  /** Pursuit cost ceiling in `local currency` (API `bidBudget`). */
  bidBudget?: number | null;
  accountPotential?: OpportunityAccountPotential;
  existingRelationship?: OpportunityExistingRelationship;
}

export interface OpportunityAccountSummary {
  id: string;
  name: string;
  industry?: IndustryDto;
  company?: CompanyDto;
}

export interface Opportunity extends OpportunityFormData {
  id: string;
  /** Miro board URL (AppApi `miroWebUrl`; use PUT …/links to update). */
  miroWebUrl?: string;
  /** Furthest wizard step reached (from AppApi `step`). */
  wizardStep: OpportunityWizardStep;
  /** Workspace that owns this opportunity (always set by AppApi). */
  workspaceId: string;
  accountId?: string;
  account?: OpportunityAccountSummary;
  /** SharePoint workspace root folder name (set at workspace create; immutable). */
  sharePointFolderName?: string;
  /** SharePoint workspace root folder URL when storage is SharePoint. */
  sharePointFolderWebUrl?: string;
  /** Dynamics model-driven app URL for the linked CRM opportunity. */
  crmOpportunityWebUrl?: string;
  createdAt: string;
  updatedAt: string;
  /** Sales pipeline stage (from AppApi `salesStage`). */
  stage: OpportunityPipelineStage;
  closeProb?: number | null;
  milestones?: OpportunityMilestones;
  assessmentDecision?: OpportunityAssessmentDecision;
  /** Locked after explicit completion (Go / No-Go); qualification writes are blocked. */
  isCompleted?: boolean;
  completedAt?: string;
  /** Where the workspace files currently live. Absent until the API classifies storage. */
  workspacePlacement?: WorkspaceStoragePlacement;
}

/** Matches AppApi `WorkspaceStoragePlacement`. */
export type WorkspaceStoragePlacement = 'Blob' | 'Qualification' | 'Bid' | 'LegacyProposals';
